import { Button, Col, Flex, Form, Row, Select } from "antd";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import type { CSSProperties } from "styled-components";
import type { InfinitySelectOption } from "~components/InfinitySelect";
import { SelectConference } from "~components/SelectConference/SelectConference.tsx";
import { SelectDivision } from "~components/SelectDivision/SelectDivision.tsx";
import { Uniforms } from "~components/Uniforms/Uniforms.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { SeasonEntityStatuses } from "~enums/seasonEntityStatuses.ts";
import { UniformTypes } from "~enums/uniformTypes.ts";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import { useGetSeasonEntity } from "./hooks/useGetSeasonEntity";
import { useUpsertSeasonEntity } from "./hooks/useUpsertSeasonEntity";

const SeasonEntityEditPage = () => {
  const { data: seasonEntity, isLoading } = useGetSeasonEntity();
  const { customerId, sport, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { mutateAsync: upsertSeasonEntity, isPending: isUpsertingSeasonEntityInProgress } = useUpsertSeasonEntity();
  const [currentConferenceId, setCurrentConferenceId] = useState<string | undefined>(seasonEntity?.conference?.conferenceId);
  const pageTitle = seasonEntity
    ? `${seasonEntity?.season?.nameLocal} - ${seasonEntity?.entity?.nameFullLocal}`
    : formatMessage({ id: "loading" });
  const formInitValues = {
    status: seasonEntity?.status,
    conference: seasonEntity?.conference?.conferenceId
      ? {
          label: seasonEntity.conference.nameLocal,
          value: seasonEntity.conference.conferenceId,
        }
      : null,
    division: seasonEntity?.division?.divisionId
      ? {
          label: seasonEntity.division.nameLocal,
          value: seasonEntity.division.divisionId,
        }
      : null,
  };

  const onConferenceChangedHandler = (conferenceOption: InfinitySelectOption | null) => {
    form.setFieldValue("division", null);
    setCurrentConferenceId(isEmpty(conferenceOption?.value) ? undefined : conferenceOption?.value);
  };

  const onUpdateButtonClick = useCallback(async () => {
    const formValues = (await form.validateFields()) ?? {};

    await upsertSeasonEntity({
      entityId: seasonEntity?.entityId,
      status: formValues.status,
      conferenceId: formValues.conference?.value ?? null,
      divisionId: formValues.division?.value ?? null,
    });
  }, [form, upsertSeasonEntity, seasonEntity]);

  useEffect(() => {
    setCurrentConferenceId(seasonEntity?.conference?.conferenceId);
  }, [seasonEntity]);

  const formItemSharedStyles: CSSProperties = { marginBottom: "5px" };

  return (
    <Page title={pageTitle}>
      <LoaderContainer loading={isLoading}>
        <Row>
          <Col span={6}>
            <Flex vertical gap={"middle"}>
              <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} initialValues={formInitValues}>
                <Form.Item
                  name={"status"}
                  label={formatMessage({ id: "status" })}
                  tooltip={formatMessage({ id: "form.help.status.item" }, { item: formatMessage({ id: "entity" }) })}
                  style={formItemSharedStyles}
                  rules={[{ max: 150, required: true }]}
                >
                  <Select
                    options={Object.values(SeasonEntityStatuses).map((x) => ({
                      label: x,
                      value: x,
                    }))}
                  />
                </Form.Item>
                <Form.Item name={"conference"} label={formatMessage({ id: "conference" })} style={formItemSharedStyles}>
                  <SelectConference placeholder={""} disabled={false} onChange={onConferenceChangedHandler} />
                </Form.Item>
                <Form.Item name={"division"} label={formatMessage({ id: "division" })} style={formItemSharedStyles}>
                  <SelectDivision conferenceId={currentConferenceId} placeholder={""} disabled={!currentConferenceId} />
                </Form.Item>
              </Form>
              <Flex justify={"flex-end"} gap={"large"}>
                <PermissionCheck permissionKeys={[PermissionNames.FIXTURE_ENTITY]} permissionOptions={[PermissionOperations.UPDATE]}>
                  <Button
                    type="primary"
                    onClick={onUpdateButtonClick}
                    disabled={isUpsertingSeasonEntityInProgress || !seasonEntity?.entityId}
                  >
                    {formatMessage({ id: "entity.update" })}
                  </Button>
                </PermissionCheck>
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/teams`}>
                  <Button type="primary">{formatMessage({ id: "close" })}</Button>
                </Link>
              </Flex>
            </Flex>
          </Col>
          <Col offset={4} span={10}>
            {seasonEntity?.entityId && <Uniforms baseType={UniformTypes.Entity} baseId={seasonEntity.entityId} />}
          </Col>
        </Row>
      </LoaderContainer>
    </Page>
  );
};

export default SeasonEntityEditPage;
